import React, { useEffect, useState } from 'react';
import { getFormCleanup, saveFormCleanup, runFormCleanupNow, getFormStats } from '../api';
import { CalendarClock, Play, Save, Trash2, FileText, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function Toggle({ on, onClick }) {
  return <button type="button" className={`switch${on ? ' on' : ''}`} onClick={onClick} aria-pressed={on} />;
}

export default function FormCleanup() {
  const [cfg, setCfg]       = useState(null);
  const [stats, setStats]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const loadStats = () => getFormStats().then(setStats).catch(() => setStats([]));
  useEffect(() => {
    getFormCleanup().then(setCfg).catch(console.error);
    loadStats();
  }, []);

  const set = (patch) => setCfg(prev => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try { setCfg(await saveFormCleanup(cfg)); toast.success('Cleanup schedule saved'); }
    catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!window.confirm('Permanently delete matching Elementor submissions on all sites now? This cannot be undone.')) return;
    setRunning(true);
    try {
      const r = await runFormCleanupNow();
      toast.success(`Deleted ${r.deleted} submission(s) across ${r.sites} site(s)`);
      getFormCleanup().then(setCfg);
      loadStats();
    } catch { toast.error('Run failed'); }
    finally { setRunning(false); }
  };

  const targeted = (s) => cfg?.mode === 'all' ? s.total : s.older_than;
  const totalTargeted = (stats || []).reduce((n, s) => n + (s.available ? targeted(s) : 0), 0);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Form Cleanup</h1>
          <p>Auto-delete Elementor form submissions — permanently</p>
        </div>
        <div className="header-actions">
          <Button className="btn danger" onClick={runNow} loading={running} disabled={!cfg}>
            <Trash2 size={14} /> {running ? 'Deleting…' : 'Delete now'}
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Permanent-delete warning */}
        <div className="card" style={{ borderColor: 'var(--amber)', background: 'var(--amber-bg)', marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertTriangle size={16} color="var(--amber)" />
            <div style={{ flex: 1, fontSize: 12.5, color: 'var(--amber-text)' }}>
              Submissions are removed directly from the database — <strong>nothing is kept in trash</strong>. This cannot be undone. Requires the <strong>WP Manager Agent</strong> plugin (v2+) and Elementor Pro on each site.
            </div>
          </div>
        </div>

        <div className="two-col">
          {/* ── Schedule config ── */}
          <div className="card">
            <div className="card-head">
              <h3><CalendarClock size={14} /> Cleanup schedule</h3>
              <Button className="btn sm" onClick={runNow} loading={running} disabled={!cfg}><Play size={12} /> Run now</Button>
            </div>
            <div style={{ padding: '6px 18px 16px' }}>
              {!cfg ? <div style={{ padding: '14px 0', color: 'var(--text3)', fontSize: 13 }}>Loading…</div> : (
                <>
                  <div className="setting-row">
                    <div>
                      <div className="sr-label">Enable scheduled cleanup</div>
                      <div className="sr-sub">{cfg.last_run ? `Last run: ${new Date(cfg.last_run).toLocaleString()} · ${cfg.last_deleted} deleted` : 'Never run yet'}</div>
                    </div>
                    <Toggle on={cfg.enabled} onClick={() => set({ enabled: !cfg.enabled })} />
                  </div>

                  {/* Mode */}
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text3)', fontWeight: 600, margin: '14px 0 8px' }}>What to delete</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', background: cfg.mode === 'older_than' ? 'var(--accent-bg)' : 'var(--bg3)', border: `1px solid ${cfg.mode === 'older_than' ? 'var(--accent2)' : 'var(--border)'}` }}>
                      <input type="radio" checked={cfg.mode === 'older_than'} onChange={() => set({ mode: 'older_than' })} style={{ accentColor: 'var(--accent)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Older than a number of days</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>Keep recent submissions, delete the rest</div>
                      </div>
                    </label>
                    {cfg.mode === 'older_than' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12 }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>Delete older than</span>
                        <input type="number" min={1} value={cfg.retention_days}
                          onChange={e => set({ retention_days: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                          style={{ width: 80, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 8, padding: '6px 10px', color: 'var(--text)', fontSize: 13 }} />
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>days</span>
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', background: cfg.mode === 'all' ? 'var(--red-bg)' : 'var(--bg3)', border: `1px solid ${cfg.mode === 'all' ? 'var(--red)' : 'var(--border)'}` }}>
                      <input type="radio" checked={cfg.mode === 'all'} onChange={() => set({ mode: 'all' })} style={{ accentColor: 'var(--red)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Delete ALL submissions</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>Every run wipes all submissions — use with care</div>
                      </div>
                    </label>
                  </div>

                  {/* When */}
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text3)', fontWeight: 600, margin: '16px 0 8px' }}>When to run</div>
                  <div style={{ display: 'grid', gridTemplateColumns: cfg.frequency === 'weekly' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8 }}>
                    <div className="field" style={{ margin: 0 }}>
                      <label>Frequency</label>
                      <select value={cfg.frequency} onChange={e => set({ frequency: e.target.value })}>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                    {cfg.frequency === 'weekly' && (
                      <div className="field" style={{ margin: 0 }}>
                        <label>Day</label>
                        <select value={cfg.day_of_week} onChange={e => set({ day_of_week: parseInt(e.target.value, 10) })}>
                          {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="field" style={{ margin: 0 }}>
                      <label>Time (server)</label>
                      <select value={cfg.run_hour} onChange={e => set({ run_hour: parseInt(e.target.value, 10) })}>
                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                      </select>
                    </div>
                  </div>

                  <Button className="btn primary" style={{ marginTop: 16 }} onClick={save} loading={saving}>
                    <Save size={13} /> {saving ? 'Saving…' : 'Save schedule'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* ── Per-site submissions ── */}
          <div className="card">
            <div className="card-head">
              <h3><FileText size={14} /> Submissions per site</h3>
              {cfg && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{totalTargeted.toLocaleString()} will be deleted</span>}
            </div>
            {!stats ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
            ) : (
              <table className="tbl">
                <thead><tr><th>Site</th><th>Total</th><th>Older than {cfg?.retention_days ?? 30}d</th><th>Add-on</th></tr></thead>
                <tbody>
                  {stats.map(s => (
                    <tr key={s.site_id}>
                      <td className="bold">{s.site_name}</td>
                      <td>{s.available ? s.total.toLocaleString() : '—'}</td>
                      <td style={{ color: s.available && s.older_than > 0 ? 'var(--amber-text)' : 'var(--text3)' }}>
                        {s.available ? s.older_than.toLocaleString() : '—'}
                      </td>
                      <td>
                        {s.available
                          ? <span className="badge green"><CheckCircle2 size={11} /> Ready</span>
                          : <span className="badge gray"><XCircle size={11} /> Not installed</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
